import { v4 as uuidv4 } from 'uuid';
import logger from 'utils/logger';
import { DEVICE_ID_PERSIST_KEY, getPersistentData, setPersistentData } from 'utils/localstorage';

const DB_NAME = 'audiorista-device';
const STORE_NAME = 'device';
const DEVICE_ID_KEY = 'id';
// Safari has shipped versions where indexedDB.open() never settles, so never wait on it for long
const DB_TIMEOUT_MS = 1000;
const MAX_DEVICE_ID_LENGTH = 128;

const isValidDeviceId = (value) =>
  typeof value === 'string' && value.length > 0 && value.length <= MAX_DEVICE_ID_LENGTH;

const withTimeout = (promise) =>
  Promise.race([
    promise,
    new Promise((resolve, reject) => {
      setTimeout(() => reject(new Error('IndexedDB timed out')), DB_TIMEOUT_MS);
    }),
  ]);

const openDatabase = () =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });

const runInStore = async (mode, operation) => {
  if (!window.indexedDB) {
    return undefined;
  }

  const db = await openDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const request = operation(transaction.objectStore(STORE_NAME));

      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
};

// Storage can be disabled, full or blocked (private windows), so every access is best effort
const attempt = async (description, operation) => {
  try {
    return await operation();
  } catch (err) {
    logger.error('device-id', description, err?.message);
    return undefined;
  }
};

const readFromLocalStorage = () => attempt('Reading localStorage', () => getPersistentData(DEVICE_ID_PERSIST_KEY));

const writeToLocalStorage = (deviceId) =>
  attempt('Writing localStorage', () => {
    setPersistentData(DEVICE_ID_PERSIST_KEY, deviceId);

    return true;
  });

const readFromIndexedDB = () =>
  attempt('Reading IndexedDB', () => withTimeout(runInStore('readonly', (store) => store.get(DEVICE_ID_KEY))));

const writeToIndexedDB = (deviceId) =>
  attempt('Writing IndexedDB', async () => {
    // runInStore resolves undefined when there is no IndexedDB to write to at all
    const storedKey = await withTimeout(runInStore('readwrite', (store) => store.put(deviceId, DEVICE_ID_KEY)));

    return storedKey !== undefined;
  });

/**
 * The id is kept in both localStorage and IndexedDB, so clearing one of them doesn't make the browser look
 * like a new device. Whichever copy is missing is restored from the other; localStorage wins if they differ.
 *
 * A brand new id is only handed out once it is actually stored somewhere. A browser that cannot remember it
 * would otherwise claim a device slot under an id it can never release, and be blocked by itself next time.
 */
const resolveDeviceId = async ({ create }) => {
  const [fromLocalStorage, fromIndexedDB] = await Promise.all([readFromLocalStorage(), readFromIndexedDB()]);

  const deviceId = [fromLocalStorage, fromIndexedDB].find(isValidDeviceId);

  if (deviceId) {
    if (fromLocalStorage !== deviceId) {
      writeToLocalStorage(deviceId);
    }

    if (fromIndexedDB !== deviceId) {
      writeToIndexedDB(deviceId);
    }

    return deviceId;
  }

  if (!create) {
    return null;
  }

  const newDeviceId = uuidv4();
  const inIndexedDB = writeToIndexedDB(newDeviceId);

  // localStorage is synchronous, so only wait on IndexedDB when it is the last hope
  if (await writeToLocalStorage(newDeviceId)) {
    return newDeviceId;
  }

  return (await inIndexedDB) ? newDeviceId : null;
};

let deviceIdRequest;

/** This browser's device id, created on first use. Never rejects. */
export const getDeviceId = () => {
  if (!deviceIdRequest) {
    deviceIdRequest = resolveDeviceId({ create: true });
  }

  return deviceIdRequest;
};

/** This browser's device id if it already has one, otherwise null. Never rejects. */
export const getStoredDeviceId = () => deviceIdRequest ?? resolveDeviceId({ create: false });
